import {
  currencySign,
  daysText,
  fmtNum,
  lei,
  money,
  numInput,
  packageChecks,
  packageCost,
  parseNum,
  perUnitMonth,
  type Currency,
  type OfferPackage,
  type PackageLine,
  type RfqItem,
  t,
} from '@procumi/shared';

/**
 * Oferta pe set pentru o poziție, în formularul de ofertă. Câmpurile sunt text
 * (ca omul să poată scrie „4.045,99”), iar `packageOf` le transformă în pachet
 * când se salvează. Liniile vin doar din citirea automată: sunt dovada și baza
 * verificării, nu ceva de retastat de mână.
 */

export interface PackageDraft {
  total: string;
  /** chirie (cu perioadă) sau vânzare */
  rent: boolean;
  periodDays: string;
  minDays: string;
  coveredQty: string;
  oneTime: string;
  replacement: string;
  excludes: string;
  lines: PackageLine[];
}

const txt = (n: number | null | undefined) => (n == null ? '' : numInput(n));

export function draftOf(pkg: OfferPackage | undefined, item: RfqItem): PackageDraft {
  if (!pkg) {
    const rent = item.rentDays != null;
    return {
      total: '',
      rent,
      periodDays: rent ? '30' : '',
      minDays: '',
      coveredQty: '',
      oneTime: '',
      replacement: '',
      excludes: '',
      lines: [],
    };
  }
  return {
    total: txt(pkg.total),
    rent: pkg.periodDays != null,
    periodDays: txt(pkg.periodDays),
    minDays: txt(pkg.minDays),
    coveredQty: txt(pkg.coveredQty),
    oneTime: pkg.oneTime ? txt(pkg.oneTime) : '',
    replacement: txt(pkg.replacement),
    excludes: pkg.excludes,
    lines: pkg.lines,
  };
}

const num = (v: string): number | null => (v.trim() ? parseNum(v) : null);

/** Pachetul din câmpuri, sau textul greșelii de arătat omului. */
export function packageOf(d: PackageDraft): OfferPackage | string {
  const total = num(d.total);
  if (total == null || total <= 0) return t('Scrie totalul pachetului.');
  const period = d.rent ? num(d.periodDays) : null;
  if (d.rent && (period == null || period < 1 || !Number.isInteger(period))) {
    return t('Scrie câte zile acoperă totalul (30 pentru o lună).');
  }
  const minDays = num(d.minDays);
  if (minDays != null && (minDays < 0 || !Number.isInteger(minDays))) {
    return t('Chiria minimă e în zile întregi.');
  }
  const oneTime = num(d.oneTime) ?? 0;
  if (oneTime < 0) return t('Costurile o singură dată nu pot fi negative.');
  const covered = num(d.coveredQty);
  const replacement = num(d.replacement);
  return {
    total,
    periodDays: d.rent ? period : null,
    minDays: d.rent ? minDays : null,
    coveredQty: covered != null && covered > 0 ? covered : null,
    oneTime,
    replacement: replacement != null && replacement > 0 ? replacement : null,
    excludes: d.excludes.trim(),
    lines: d.lines,
  };
}

export function PackageEditor({
  item,
  draft,
  currency,
  fx,
  onChange,
}: {
  item: RfqItem;
  draft: PackageDraft;
  currency: Currency;
  /** lei pentru o unitate din moneda ofertei; null cât timp lipsește cursul */
  fx: number | null;
  onChange: (d: PackageDraft) => void;
}) {
  const set = (p: Partial<PackageDraft>) => onChange({ ...draft, ...p });
  const sign = currencySign(currency);
  const pkg = packageOf(draft);
  const ok = typeof pkg !== 'string';
  const wantsRent = item.rentDays != null;
  const cost = ok ? packageCost(pkg, item.rentDays) : null;
  const perMonth = ok ? perUnitMonth(pkg) : null;
  const checks = ok ? packageChecks(pkg, item, currency) : [];
  const mismatch = ok && wantsRent !== draft.rent;

  return (
    <div className="pkg">
      <div className="pkg-grid">
        <label className="f">
          {t('Regim')}
          <select
            className="inp"
            value={draft.rent ? 'rent' : 'buy'}
            onChange={(e) =>
              set({
                rent: e.target.value === 'rent',
                periodDays: e.target.value === 'rent' && !draft.periodDays ? '30' : draft.periodDays,
              })
            }
          >
            <option value="rent">{t('Chirie')}</option>
            <option value="buy">{t('Vânzare')}</option>
          </select>
        </label>
        <label className="f">
          {draft.rent ? t('Chirie pe perioadă ({0})', sign) : t('Total set ({0})', sign)}
          <input
            className="inp"
            inputMode="decimal"
            value={draft.total}
            onChange={(e) => set({ total: e.target.value })}
          />
        </label>
        {draft.rent ? (
          <>
            <label className="f">
              {t('Pentru câte zile')}
              <input
                className="inp"
                inputMode="numeric"
                value={draft.periodDays}
                onChange={(e) => set({ periodDays: e.target.value })}
              />
            </label>
            <label className="f">
              {t('Chirie minimă (zile)')}
              <input
                className="inp"
                inputMode="numeric"
                value={draft.minDays}
                onChange={(e) => set({ minDays: e.target.value })}
              />
            </label>
          </>
        ) : null}
        <label className="f">
          {t('Acoperă ({0})', item.unit)}
          <input
            className="inp"
            inputMode="decimal"
            value={draft.coveredQty}
            placeholder={fmtNum(item.qty)}
            onChange={(e) => set({ coveredQty: e.target.value })}
          />
        </label>
        <label className="f">
          {t('O singură dată ({0})', sign)}
          <input
            className="inp"
            inputMode="decimal"
            value={draft.oneTime}
            placeholder={t('transport, montaj')}
            onChange={(e) => set({ oneTime: e.target.value })}
          />
        </label>
        {draft.rent ? (
          <label className="f">
            {t('Valoare de înlocuire ({0})', sign)}
            <input
              className="inp"
              inputMode="decimal"
              value={draft.replacement}
              onChange={(e) => set({ replacement: e.target.value })}
            />
          </label>
        ) : null}
        <label className="f">
          {t('Nu include')}
          <input
            className="inp"
            value={draft.excludes}
            placeholder={t('ex. transport')}
            onChange={(e) => set({ excludes: e.target.value })}
          />
        </label>
      </div>

      {ok && cost != null ? (
        <p className="pkg-cost">
          {wantsRent ? t('Pentru {0} cerute: ', daysText(item.rentDays!)) : t('Cost: ')}
          <b>{money(cost, currency)}</b>
          {currency !== 'RON' && fx ? <> = {lei(cost * fx)}</> : null}
          {perMonth != null ? (
            <span className="muted">{t('· {0}/{1}/lună', money(perMonth, currency), item.unit)}</span>
          ) : null}
        </p>
      ) : null}
      {mismatch ? (
        <p className="pnote warn">
          {wantsRent
            ? t('S-a cerut chirie, iar oferta e de vânzare: rămâne la ofertă, dar nu intră în comparație.')
            : t('S-a cerut cumpărare, iar oferta e de chirie: rămâne la ofertă, dar nu intră în comparație.')}
        </p>
      ) : null}
      {checks.length ? (
        <ul className="checks-list">
          {checks.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      ) : null}
      {draft.lines.length ? (
        <details className="pkg-lines">
          <summary>
            {t(
              '{0} {1} din ofertă · suma {2}',
              draft.lines.length,
              draft.lines.length === 1 ? 'linie' : 'linii',
              money(
                draft.lines.reduce((a, l) => a + l.amount, 0),
                currency,
              ),
            )}
          </summary>
          <table>
            <tbody>
              {draft.lines.map((l, i) => (
                <tr key={i}>
                  <td className="muted">{l.code}</td>
                  <td>{l.desc}</td>
                  <td className="n">{l.qty == null ? '' : fmtNum(l.qty)}</td>
                  <td className="n">{money(l.amount, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn small" onClick={() => set({ lines: [] })}>
            {t('Scoate liniile')}
          </button>
        </details>
      ) : null}
      {!ok && draft.total.trim() ? <p className="pnote warn">{pkg}</p> : null}
    </div>
  );
}
